/// <amd-module name="TFS/Admin/Views/OrganizationOverview/OrganizationOverview" />
import React = require("react");

import AdminResources = require("TFS/Admin/Views/OrganizationOverview/Resources");
import { Components, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Button } from "VSSUI/Button";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { Toggle } from "VSSUI/Toggle";
import { Dropdown } from "VSSUI/DropDown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IListBoxItem } from "VSSUI/ListBox";
import { ObservableValue } from "VSS/Core/Observable";
import { KeyCode } from "VSSUI/Util";
import { getOrganizationClient } from "TFS/Admin/Common/RestClient/Organization";
import { ImageData, InputFileSelect } from "TFS/Admin/Common/Contracts/AvatarImagePicker.types";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Link } from "VSSUI/Link";
import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { VssPersona } from "VSSUI/VssPersona";
import { wait } from "VSS/Core/Util/Promise";
import { Dialog } from "VSSUI/Dialog";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IOrganizationOverviewData, IOrganizationOverviewDelayLoadData, IOwner } from "Contracts/DataModels";
import { ChangeOwner } from "Components/ChangeOwner/ChangeOwner";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { validateOrganizationName } from "TFS/Admin/Common/Util/NameValidator";
import { format } from "VSS/Core/Util/String";
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { OrganizationOverviewSource } from "TFS/Admin/Views/OrganizationOverview/Sources/OrganizationOverviewSources";
import { SoftDeletedProjects } from "Components/SoftDeletedProjects/SoftDeletedProjects";
import { DeleteOrganization } from "Components/DeleteOrganization/DeleteOrganization";
import { ITimeZone } from "Contracts/DataModels";
import { ContentSize } from "VSSUI/Callout";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { getHostAcquisitionClient } from "TFS/Admin/Common/RestClient/HostAcquisition";
import { TeamFoundationHostType, IVssRestClientOptions } from "VSS/Platform/Context";
import { BearerTokenProvider } from "VSS/Platform/Authentication";
import { Intersection } from "VSSUI/Intersection";
import { IOrganizationTokenData } from "Contracts/DataModels";
import {
    OrganizationDataProvidersContributions,
    OrganizationServiceInstanceTypes,
    ConstantNumbers,
    FeatureConstants
} from "TFS/Admin/Views/OrganizationOverview/Constants/Constants";
import { IRenameDialogProps } from "TFS/Admin/Common/Controls/RenameDialog/RenameDialog.Props";
import { announce } from "VSS/Core/Util/Accessibility";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ResourceUsageComponent } from "TFS/Admin/Common/Components/ResourceUsage";

export interface IOrganizationOverviewComponentState {
    data: IOrganizationOverviewData;
    delayLoadData?: IOrganizationOverviewDelayLoadData;
    error?: string;
    success?: string;
    domainToggle: boolean;
    timeZone: ITimeZone;
    showRenameDialog?: boolean;
    updateInProgress?: boolean;
    showMigrationDialog?: boolean;
    showChangeImageDialog?: boolean;
    imageUpdateInProgress?: boolean;
}

export class OrganizationOverviewComponent extends VssComponent<{}, IOrganizationOverviewComponentState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private privacyUrl: ObservableValue<string>;
    private name: ObservableValue<string>;
    private description: ObservableValue<string>;
    private avatarUrl: string;
    private options: any[];
    private isEdited: boolean;
    private errorMessage: string;
    private dataProviderError = AdminResources.DataProviderError;
    private _timeZoneSelection = new DropdownSelection();

    public constructor(props: {}, context?: any) {
        super(props, context);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");

        this.state = {
            data: contributionService.getData<IOrganizationOverviewData>(OrganizationDataProvidersContributions.EditDataProviderId)!,
            domainToggle: false,
            timeZone: { id: "", displayName: "" }
        };

        if (this.state.data) {
            this.privacyUrl = new ObservableValue<string>(this.state.data.privacyUrl);
            this.name = new ObservableValue<string>(this.state.data.name);
            this.description = new ObservableValue<string>(this.state.data.description);
            this.avatarUrl = this.state.data.avatarUrl;
            this.state.timeZone.id = this.state.data.timeZone.id;
            this.state.timeZone.displayName = this.state.data.timeZone.displayName;
            this.options = this._getOptions(false);
        }
        this._timeZoneSelection.select(0);
    }

    public render(): JSX.Element {
        const data = this.state.data;
        if (!data) {
            return (
                <div className="overview-content">
                    <MessageBar className="organization-message top-margin overview-content" severity={MessageBarSeverity.Error}>
                        {this.dataProviderError}
                    </MessageBar>
                </div>
            );
        }
        this.errorMessage = this._getNameErrorMessage();
        this._onChangeOwner = this._onChangeOwner.bind(this);
        const delayLoadData = this.state.delayLoadData;
        const subheading = (heading: string) => <h5 className="fontSizeM overview-heading">{heading}</h5>;

        this.isEdited = this._checkChangeState();
        if (this.options.length <= 1 && this.state.delayLoadData) {
            this.options = this._getOptions(true);
        }

        return (
            <Intersection>
                <div className="overview-content">
                    <div className="title-l">{AdminResources.OrganizationOverviewHeading}</div>
                    {this.state.error ? (
                        <MessageBar
                            className="organization-message"
                            severity={MessageBarSeverity.Error}
                            onDismiss={() => this.setState({ error: undefined })}
                        >
                            {this.state.error}
                        </MessageBar>
                    ) : null}
                    {this.state.success ? (
                        <MessageBar
                            className="organization-message"
                            severity={MessageBarSeverity.Success}
                            onDismiss={() => this.setState({ success: undefined })}
                        >
                            {this.state.success}
                        </MessageBar>
                    ) : null}
                    <div className="organization-overview flex-row">
                        <div className="overview-edit-fields">
                            <div className="organization-name flex-row">
                                <div className="flex-column">
                                    {subheading(AdminResources.NameHeading)}
                                    <FormItem className="organization-name-field" message={this.errorMessage} error={this.errorMessage !== ""}>
                                        <TextField
                                            value={this.name}
                                            onChange={(e, newValue) => this._onChange(this.name, newValue)}
                                            ariaLabel={AdminResources.AriaOrganizationName}
                                            disabled={!data.hasModifyPermissions}
                                        />
                                    </FormItem>
                                    <div className="organization-new-domain flex-row flex-center">
                                        {this.state.delayLoadData && this.state.delayLoadData.showDomainMigration ? (
                                            <div>
                                                <Toggle
                                                    ariaLabel={AdminResources.AriaToggle}
                                                    checked={this.state.domainToggle}
                                                    onChange={(e, checked) => this._onToggelChange(e, checked)}
                                                    disabled={
                                                        !data.hasModifyPermissions ||
                                                        this.state.updateInProgress ||
                                                        this.state.delayLoadData.disableDomainMigration
                                                    }
                                                />
                                                <FormatComponent format={AdminResources.NewDomain} className="organization-url">
                                                    {this.state.delayLoadData.devOpsDomainUrls ? data.url : this.state.delayLoadData.targetDomainUrl}
                                                </FormatComponent>
                                            </div>
                                        ) : (
                                            <FormatComponent format={AdminResources.CurrentDomain}>{data.url}</FormatComponent>
                                        )}
                                    </div>
                                    <Link target="_blank" rel="noopener noreferrer" href={AdminResources.DomainLearnMoreLink}>
                                        {AdminResources.DomainLearnMore}
                                    </Link>
                                </div>

                                {isFeatureFlagEnabled(this.context.pageContext, FeatureConstants.EditableOrganizationAvatar, false) ? this.getNewOrganizationImage() : this.getOldOrganizationImage()}

                            </div>
                            {subheading(AdminResources.PrivacyUrlHeading)}
                            <TextField
                                ariaLabel={AdminResources.AriaPrivacyURL}
                                className="organization-privacy-url-field"
                                value={this.privacyUrl}
                                multiline={false}
                                onChange={(e, newValue) => this._onChange(this.privacyUrl, newValue)}
                                disabled={!data.hasModifyPermissions}
                            />
                            <Link target="_blank" rel="noopener noreferrer" href={AdminResources.PrivacyUrlLearnMoreLink}>
                                {AdminResources.PrivacyUrlLearnMore}
                            </Link>
                            {subheading(AdminResources.DescriptionHeading)}
                            <TextField
                                ariaLabel={AdminResources.AriaDescription}
                                className="organization-description"
                                value={this.description}
                                placeholder={AdminResources.DescriptionPlaceholder}
                                multiline={true}
                                onChange={(e, newValue) => this._onChange(this.description, newValue)}
                                disabled={!data.hasModifyPermissions}
                            />
                            {subheading(AdminResources.TimeZone)}
                            <Dropdown
                                className="organization-timezone"
                                ariaLabel={AdminResources.AriaTimeZone}
                                items={this.options}
                                onSelect={this.onComboChanged}
                                selection={this._timeZoneSelection}
                                disabled={!data.hasModifyPermissions}
                            />
                            {data.geography ? (
                                <>
                                    {subheading(AdminResources.Geography)}
                                    <div className="organization-geography">{data.geography}</div>
                                    <Link target="_blank" rel="noopener noreferrer" href={AdminResources.GeographyLearnMoreLink}>
                                        {AdminResources.GeographyLearnMore}
                                    </Link>
                                </>
                            ) : null
                            }
                            {subheading(AdminResources.Region)}
                            <div>{data.region}</div>
                            {data.hasModifyPermissions ? (
                                <div className="organization-save flex-row flex-center top-margin">
                                    <Button
                                        className="organization-edit-button"
                                        primary={true}
                                        disabled={!this.isEdited || this.state.updateInProgress}
                                        onClick={this._onSaveClick}
                                    >
                                        {AdminResources.SaveButton}
                                    </Button>
                                    {this.state.updateInProgress ? <Spinner className="small-left-margin" size={SpinnerSize.large} /> : null}
                                    <Icon className="small-left-margin" iconName="Error" />
                                    <span className="small-left-margin edit-warning">{AdminResources.ChangesWarning}</span>
                                </div>
                            ) : null}
                            <div className="overview-divider" />
                        </div>
                    </div>

                    {this.state.showRenameDialog ? (
                        <WrappedComponent<IRenameDialogProps>
                            dependencies={["ms.vss-admin-web.admin-common"]}
                            wrappedType={"ms.vss-admin-web.rename-dialog"}
                            wrappedProps={{
                                name: data.name,
                                onCancel: () => this.setState({ showRenameDialog: false }),
                                onRename: this._onRename,
                                renameDialogTitle: AdminResources.RenameOrganizationDialogTitle,
                                renameDialog1: AdminResources.RenameOrganizationDialog1,
                                renameDialog2: AdminResources.RenameOrganizationDialog2,
                                renameDialog2Link: AdminResources.RenameOrganizationDialog2Link,
                                renameConfirmationText: AdminResources.RenameOrganizationConfirmationText,
                                renameNewName: AdminResources.RenameOrganizationNewName,
                                invalidNameMessage: AdminResources.InvalidOrganizationNameMessage,
                                newName: this.name.value,
                                url: this.state.data.url,
                                validateName: validateOrganizationName
                            }}
                        />
                    ) : null}

                    {this.state.showMigrationDialog ? (
                        <Dialog
                            onDismiss={() => {
                                this._onCancelToggle();
                            }}
                            contentSize={ContentSize.Small}
                            className="organization-migration-dialog"
                            titleProps={{
                                text: AdminResources.ChangeOrganizationDomain,
                                className: "fontWeightSemiBold"
                            }}
                            footerButtonProps={[
                                {
                                    text: AdminResources.Cancel,
                                    onClick: () => {
                                        this._onCancelToggle();
                                    }
                                },
                                {
                                    disabled: false,
                                    text: AdminResources.SaveButton,
                                    onClick: this._saveMigration,
                                    primary: true
                                }
                            ]}
                        >
                            {AdminResources.ChangeOrganizationText}
                        </Dialog>
                    ) : null}

                    {isFeatureFlagEnabled(this.context.pageContext, "ResourceUsage.Service.TeamProjectCollection", false) ?
                    (<><div><ResourceUsageComponent source='OrganizationOverview' projectId={""} resourceUsageName='Organization Usage Limit'/>
                    <div className="overview-divider" /></div></>) : null }

                    {delayLoadData ? (
                        <div>
                            <ChangeOwner
                                pageContext={this.context.pageContext}
                                onChangeOwner={this._onChangeOwner}
                                organizationName={this.state.data.name}
                                owner={delayLoadData.currentOwner}
                                hasModifyPermissions={delayLoadData.hasModifyPermissions}
                                hasDeletePermissions={delayLoadData.hasDeletePermissions}
                                isOwner={delayLoadData.isOwner}
                                organizationTakeover={delayLoadData.organizationTakeover}
                                organizationId={data.id}
                            />

                            <SoftDeletedProjects pageContext={this.context.pageContext} />

                            {delayLoadData.hasDeletePermissions ? (
                                <DeleteOrganization
                                    pageContext={this.context.pageContext}
                                    organizationId={this.state.data.id}
                                    organizationName={this.state.data.name}
                                />
                            ) : null}
                        </div>
                    ) : null}

                    {this.state.showChangeImageDialog && (
                        <WrappedComponent
                            dependencies={["ms.vss-features.ui-avatar-image-picker-content"]}
                            wrappedType={"vss.avatar-image-picker"}
                            wrappedProps={{
                                onCancel: this._cancelImageUpload,
                                onSave: this._saveImageAction,
                                onFileSelected: this._onImageFileSelected,
                                onLoadDisplayAvatarUrl: this.avatarUrl,
                                title: AdminResources.ChangeImageTitle,
                                showInitialsOnImageError: true,
                                displayName: this.state.data.name,
                                defaultResetAvatarUrl: null
                            }}
                        />
                    )}
                </div>
            </Intersection>
        );
    }

    private _cancelImageUpload = (hasChanges: boolean) => {
        this.setState({ showChangeImageDialog: false });
    };

    private _saveImageAction = (resetImage: boolean, imageData: ImageData) => {
        if (resetImage) {
            this._removeImage();
        } else {
            this._saveImage(imageData);
        }
    };

    private _onImageFileSelected(error: string, imageData: ImageData): InputFileSelect {
        const result = {} as InputFileSelect;

        // File validation error from image select.
        if (error) {
            result.previewImage = false;
            result.error = error;
        } else {
            result.previewImage = true;
            result.error = "";
        }

        return result;
    };

    private _saveImage(imageData: ImageData): Promise<void> {
        this.setState({ showChangeImageDialog: false, imageUpdateInProgress: true });
        const actionTelemetryLogger = this._startActionCapture("Set Organization Avatar", {});

        let client = getOrganizationClient(this.context.pageContext);
        return client.updateCollectionAvatar(this.state.data.id, imageData.byteArray).then(
            async () => {
                this.setState({imageUpdateInProgress: false});
            },
            reason => {
                this.setState({ error: reason.message || String(reason), imageUpdateInProgress: false });
                actionTelemetryLogger.captureError(reason.message || String(reason));
            }
        );
    };

    private _removeImage(): Promise<void> {
        this.setState({ showChangeImageDialog: false, imageUpdateInProgress: true });
        const actionTelemetryLogger = this._startActionCapture("Remove Organization Avatar", {});

        let client = getOrganizationClient(this.context.pageContext);
        return client.deleteCollectionAvatar(this.state.data.id).then(
            async () => {
                this.setState({ imageUpdateInProgress: false });
            },
            reason => {
                this.setState({ error: reason.message || String(reason), imageUpdateInProgress: false });
                actionTelemetryLogger.captureError(reason.message || String(reason));
            }
        );
    };

    private _onChangeImageClick = (): void => {
        this.setState({ showChangeImageDialog: true });
    };

    private _onChangeImageKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.keyCode === KeyCode.enter || event.keyCode === KeyCode.space) {
            this._onChangeImageClick();
        }
    };

    private getOrganizationImage(): JSX.Element {
        return (<div className="vss-Persona flex-noshrink organization-persona top-margin extra-extra-large" role="img" aria-label="AzureDevOps">
            <img className="vss-Persona-content using-image" src={this.avatarUrl} alt="Organization logo" />
        </div>);
    };

    private getNewOrganizationImage(): JSX.Element {
        var data = this.state.data;

        if (this.state.imageUpdateInProgress) {
            return (<div>
                <span>
                    <Spinner size={SpinnerSize.large} className="imageSpinner" />
                </span>
            </div>);
        }

        if (data.hasModifyPermissions) {
            return (<div>
                <div
                    className="persona-list-element profile-card-tab-element image-container"
                    aria-label={AdminResources.ChangeImageClick}
                    onClick={this._onChangeImageClick}
                    data-is-focusable={true}
                    role="button"
                    tabIndex={0}
                    onKeyDown={this._onChangeImageKeyDown}
                >

                {this.getOrganizationImage()}
                <div className="overlay" />
                <Icon iconName="Edit" className="ms-font-l editIcon" />
                </div>
            </div>);
        } else {
            return this.getOrganizationImage()
        }
    };

    private getOldOrganizationImage(): JSX.Element {
        return (<VssPersona
            identityDetailsProvider={{
                getIdentityImageUrl: (size: number): string | undefined => {
                    return undefined;
                },
                getDisplayName: (): string => {
                    return this.state.data.name;
                }
            }}
            size={"extra-extra-large"}
            cssClass="organization-persona top-margin"/>);
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationOverview, Feature.OrganizationOverview, action, properties);
    };

    private _onCancelToggle = (event?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>): void => {
        this.setState({ showMigrationDialog: false, domainToggle: !this.state.domainToggle });
    };

    private _getOptions(allOptions: boolean): any[] {
        let dropdownOptions = [];
        dropdownOptions.push({ id: this.state.data.timeZone.id, text: this.state.data.timeZone.displayName });
        if (allOptions && this.state.delayLoadData) {
            for (let index in this.state.delayLoadData.allTimeZones) {
                let id = this.state.delayLoadData.allTimeZones[index].id;
                if (id !== this.state.data.timeZone.id) {
                    dropdownOptions.push({ id: id, text: this.state.delayLoadData.allTimeZones[index].displayName });
                }
            }
        }
        return dropdownOptions;
    }

    protected onComboChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>) => {
        if (item) {
            this.setState({ timeZone: { id: item.id, displayName: item.text ? item.text : "" } });
        }
    };

    private _onChange = (observable: ObservableValue<string>, newValue: string) => {
        observable.value = newValue;

        if (this.isEdited !== this._checkChangeState() || this.errorMessage !== this._getNameErrorMessage()) {
            this.forceUpdate();
        }
    };

    private _onToggelChange = async (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>, checked: boolean) => {
        this._startActionCapture("Show migration dialog", {});
        this.setState({ domainToggle: checked });
        this.setState({ showMigrationDialog: true });
    };

    private _saveMigration = async () => {
        var source = new OrganizationOverviewSource();

        var migrateDomainTelemetryHelper = this._startActionCapture("Migrate Domain", { CodexDomain: this.state.domainToggle });
        this.setState({ updateInProgress: true, showMigrationDialog: false });
        source.queueChangeDomain(this.state.domainToggle, this.context).then(
            async () => {
                migrateDomainTelemetryHelper.captureTelemetry({ Info: "Wait for domain migration" });
                await this._waitForDomainMigrationComplete(source, migrateDomainTelemetryHelper);
            },
            reason => {
                this.setState({
                    updateInProgress: false,
                    error: reason.message || String(reason),
                    domainToggle: this.state.delayLoadData ? this.state.delayLoadData.devOpsDomainUrls : !this.state.domainToggle
                });
                announce(reason.message);
                migrateDomainTelemetryHelper.captureError(reason.message);
            }
        );
    };

    private async _waitForDomainMigrationComplete(source: OrganizationOverviewSource, telemetryLogger: AdminFeatureTelemetryLogger) {
        await source.waitChangeDomainCompletion(this.context).then(
            async value => {
                if (value) {
                    this.setState({ updateInProgress: false });
                } else {
                    announce(AdminResources.FailedMigration);
                    this.setState({
                        updateInProgress: false,
                        error: AdminResources.FailedMigration,
                        domainToggle: this.state.delayLoadData ? this.state.delayLoadData.devOpsDomainUrls : !this.state.domainToggle
                    });
                    telemetryLogger.captureError("No value returned from waitChangeDomainCompletion");
                }
            },
            reason => {
                announce(reason.message);
                this.setState({
                    updateInProgress: false,
                    error: reason.message || String(reason),
                    domainToggle: this.state.delayLoadData ? this.state.delayLoadData.devOpsDomainUrls : !this.state.domainToggle
                });
                telemetryLogger.captureError(reason.message);
            }
        );
    }

    private _checkChangeState = () => {
        if (
            (localeIgnoreCaseComparer(this.name.value, this.state.data.name) === 0 &&
                this.description.value === this.state.data.description &&
                this.privacyUrl.value === this.state.data.privacyUrl &&
                this.state.timeZone.id === this.state.data.timeZone.id) ||
            this.name.value === "" ||
            !validateOrganizationName(this.name.value)
        ) {
            return false;
        } else {
            return true;
        }
    };

    private _onRename = (newName: string): void => {
        this.setState({ showRenameDialog: false });
        this._saveOrganizationDetails(true, newName);
    };

    private _onSaveClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        var actionTelemetryLogger = this._startActionCapture("Save clicked", {});

        if (localeIgnoreCaseComparer(this.name.value, this.state.data.name) !== 0) {
            this.setState({ updateInProgress: true });
            this._isNameAvailable(this.name.value)
                .then(value => {
                    if (value) {
                        this._triggerSaveDialog();
                    } else {
                        actionTelemetryLogger.captureError("Organization Name taken");
                        announce(format(AdminResources.NameIsNotAvailable, this.name.value));
                        this.setState({ updateInProgress: false, error: format(AdminResources.NameIsNotAvailable, this.name.value) });
                    }
                })
                .catch(reason => {
                    actionTelemetryLogger.captureError(reason.message);
                    this._triggerSaveDialog();
                });
        } else {
            this._saveOrganizationDetails(false);
        }
    };

    private _triggerSaveDialog() {
        this._startActionCapture("Show rename dialog", {});
        this.setState({ showRenameDialog: true, updateInProgress: false });
    }

    private _getNameErrorMessage = (): string => {
        if (this.name.value !== "" && !validateOrganizationName(this.name.value)) {
            // Error message from the validation
            return format(AdminResources.InvalidOrganizationNameMessage, this.name.value);
        } else {
            return "";
        }
    };

    private _isNameAvailable = async (name: string): Promise<boolean> => {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        let tokens = await contributionService.getDataAsync<IOrganizationTokenData>(OrganizationDataProvidersContributions.DeleteTokenDataProviderId);

        var client = getHostAcquisitionClient(this.context.pageContext, {
            hostType: TeamFoundationHostType.Deployment,
            serviceInstanceType: OrganizationServiceInstanceTypes.ServiceInstanceTypeAex,
            authTokenProvider:
                tokens && tokens.deploymentLevelSessionToken ? new BearerTokenProvider(tokens.deploymentLevelSessionToken.token) : undefined
        } as IVssRestClientOptions);
        var availability = await client.getNameAvailability(name);
        return availability.isAvailable;
    };

    private _saveOrganizationDetails = async (rename: boolean, newName?: string): Promise<void> => {
        var propertyEdits = false;
        if (this.description.value !== this.state.data.description) {
            this._startActionCapture("Update Description", {});
            propertyEdits = true;
        }
        if (this.state.timeZone.id !== this.state.data.timeZone.id) {
            this._startActionCapture("Update Time Zone", {});
            propertyEdits = true;
        }
        if (this.privacyUrl.value !== this.state.data.privacyUrl) {
            this._startActionCapture("Update Privacy Url", {});
            propertyEdits = true;
        }
        if (propertyEdits) {
            this.setState({ updateInProgress: true });
            var source = new OrganizationOverviewSource();

            var actionTelemetryLogger = this._startActionCapture("Update Organization", {});
            await source
                .updateProperties(this.state.data.id, this.context, this.description.value, this.state.timeZone.id, this.privacyUrl.value)
                .then(
                    result => {
                        if (result) {
                            this._refreshProperties();
                        } else {
                            actionTelemetryLogger.captureError("No result returned from updateProperties");
                            this.setState({ updateInProgress: false });
                        }
                    },
                    reason => {
                        actionTelemetryLogger.captureError(reason.message);
                        announce(reason.message);
                        this.setState({ updateInProgress: false, error: reason.message });
                    }
                );
        }

        if (rename && newName) {
            var actionTelemetryLogger = this._startActionCapture("Rename Organization", {});
            this.setState({ updateInProgress: true });
            var source = new OrganizationOverviewSource();
            await source.renameCollection(newName, this.context).then(
                async collection => {
                    if (collection.name !== this.state.data.name) {
                        this._refreshPageForRename(newName);
                    }
                },
                reason => {
                    this.setState({ updateInProgress: false, error: reason.message || String(reason) });
                    announce(reason.message);
                    actionTelemetryLogger.captureError(reason.message);
                }
            );
        }
    };

    private _onChangeOwner(newOwner: IOwner) {
        var delayLoadData = this.state.delayLoadData;
        var data = this.state.data;
        if (delayLoadData) {
            delayLoadData.currentOwner = newOwner;
            // for the takeover scenario, the person updating the owner could be a non-PCA and updating to themselves to handle that flow
            if (delayLoadData.currentUserId === newOwner.id) {
                delayLoadData.hasDeletePermissions = true;
                delayLoadData.hasModifyPermissions = true;
                data.hasModifyPermissions = true;
            }
            this.setState({ delayLoadData: delayLoadData, data: data });
        }
    }

    private async _refreshProperties(): Promise<void> {
        let data = this.state.data;
        data.description = this.description.value;
        data.timeZone.id = this.state.timeZone.id;
        data.timeZone.displayName = this.state.timeZone.displayName;
        data.privacyUrl = this.privacyUrl.value;
        announce(AdminResources.UpdateDetailsSuccess);
        this.setState({ data: data, updateInProgress: false, success: AdminResources.UpdateDetailsSuccess });
    }

    private async _refreshPageForRename(name?: string): Promise<void> {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        var actionTelemetryLogger = this._startActionCapture("Refresh page for rename", {});
        try {
            let i = 0;
            while (this.state.updateInProgress === true && i++ < 100) {
                var data = await contributionService.getDataAsync<IOrganizationOverviewData>(
                    OrganizationDataProvidersContributions.EditDataProviderId,
                    undefined /*RequestParameters*/,
                    true /*ignoreCache*/
                );
                if (data && data.name === name && this.state.data.url !== data.url) {
                    // customer could be using a different url than we get from the api.  Using just the name could result in a partial name match for some customers
                    if (window.location.href.includes(this.state.data.url)) {
                        this._changeUrl(this.state.data.url, data.url);
                    } else {
                        this._changeUrl(this.state.data.name, name ? name : this.state.data.name);
                    }
                }
                await wait(ConstantNumbers.ProgressPollInterval);
            }
        } catch (ex) {
            if ((ex.message as string).indexOf("404") != -1) {
                actionTelemetryLogger.captureError("Changing to rename url");
            } else {
                actionTelemetryLogger.captureError(ex.message);
            }
            this._changeUrl(this.state.data.name, name ? name : this.state.data.name);
        }
    }

    private _changeUrl = (oldName: string, newName: string) => {
        let url = window.location.href;
        url = url.replace(oldName, newName);
        window.location.href = url;
        this.setState({ updateInProgress: false });
    };

    public componentDidMount(): void {
        super.componentDidMount();

        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.OrganizationOverview);
        this._updateDelayLoad();
    }

    private async _updateDelayLoad() {
        var contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        var newdata = await contributionService.getDataAsync<IOrganizationOverviewDelayLoadData>(
            OrganizationDataProvidersContributions.DelayLoadDataProviderId
        );

        this.setState({
            delayLoadData: newdata,
            domainToggle: newdata ? newdata.devOpsDomainUrls : false
        });
    }
}

Components.add("organizationOverview", OrganizationOverviewComponent);
